import { Injectable } from '@angular/core';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from 'client/app/app.config';
import {
  RegimenRehaulV1Experiment,
  RegimenRehaulV1Result,
  RestrictedProductForPregnancyAndTeenagerExperiment,
  RegimenReorderExperiment,
  ReviewOrderPageExperiment,
  RegimenPricingExperiment,
  BottomNavConfigExperiment,
  ShopDynamicBannerExperiment,
  ReportRootCauseExperiment,
  ChatSkipLastQuestionsExperiment,
  RecentlyViewedExperiment,
  RetentionHomeRevampExperiment,
  FollowUpChatDefaultFirstAnswerExperiment,
  PrepaidDiscountExperiment,
  FupQuestionsNewUiExperiment,
  RegimenVariantsScrollViewCollapseExperiment,
  RegimenProductImageCarouselExperiment,
  RegimenPreparationTimerVariant,
} from './experiment.service.types';
import { RegimenRouteVariant } from 'client/app/user/user-view/user-regimen/regimen.types';

export interface AcqShopExperiment {
  key: 'acq_shop';
  enable?: boolean;
  variant?: {
    startDay?: number;
    endDay?: number;
    sectionOrder?: Partial<Record<'FACE' | 'HAIR' | 'BOTH', string[]>>;
    bannerProductCards?: boolean;
  };
}


@Injectable({ providedIn: 'root' })
export class ExperimentService {
  appVersion: number = null;
  webAppVersion: string = null;
  private experimentAndroidVersionForParseInterceptor: number = 207000;
  private experimentForVideoInstructions: number = 207141;
  isInternalUser: boolean = false;
  appVersionFromConfigTable: { androidApp: string, webApp: string } = {
    androidApp: null, webApp: null,
  };
  isMulticoncernUI: boolean = false;

  private _experimentsLoadedResolve: () => void;
  readonly experimentsLoaded: Promise<void> = new Promise((resolve) => {
    this._experimentsLoadedResolve = resolve;
  });

  // Experiment flags
  applyCashExperiment: boolean = false;
  globalCacheExperiment: any = null; // Store the experiment object for setEnableGlobalCache
  restrictedShopForPregnancyAndTeenagerExperiment: RestrictedProductForPregnancyAndTeenagerExperiment = null;
  addToCartVariantSelection: boolean = false;
  genderTranslationsEnabled: boolean = false;
  optimiseApiEnabled: boolean = false;
  hideIncompleteChatBottomsheet: boolean = false;
  sidePhotoNudgeInFrontFace: Record<string, unknown> = null;
  videoCallExperiment: boolean = false;
  regimenReorderExperiment: RegimenReorderExperiment = null;
  reviewOrderPageExperiment: ReviewOrderPageExperiment = null;
  shopDynamicBannerExperiment: ShopDynamicBannerExperiment = null;
  prefillAddressShiprocketEnabled: boolean = false;
  prepaidDiscountExperiment: PrepaidDiscountExperiment = null;
  newRegimenPricingExperiment: RegimenPricingExperiment;
  sharePaymentLink: boolean = false;
  bottomNavConfigExperiment: BottomNavConfigExperiment = null;
  npsFeedbackRevamp: boolean = false;
  isRetentionHomeRevampExperimentEnabled: boolean = false;
  retentionHomeRevampExperiment: RetentionHomeRevampExperiment = null;
  diet1500Experiment: any = null;

  // Regimen route refactor experiment flags
  regimenRouteExperiment: boolean = false;
  regimenRouteVariant: RegimenRouteVariant = {};
  regimenRehaulV2: boolean = false;
  regimenPreparationTimerVariant: RegimenPreparationTimerVariant = { activationDelayInSeconds: 120 };
  newInstructionUIExperiment: boolean = false;
  dietBannerV2: boolean = false;
  dietPlanV2Experiment: boolean = false;

  // Skin/Hair report experiment flags
  skinReportDrVideoExperiment: any = null;
  skinReportHeroIngredientsVariant: { numberOfIngredients: number } = null;
  treatmentPlanDescriptionExperiment: any = null;
  chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment = null;
  chatSkipLastQuestionHomeBanner: ChatSkipLastQuestionsExperiment = null;
  rootCausesExperiment: ReportRootCauseExperiment = null;
  acqShop: AcqShopExperiment | null = null;
  recentlyViewedExperiment: RecentlyViewedExperiment = null;
  followUpChatDefaultFirstAnswer: FollowUpChatDefaultFirstAnswerExperiment = null;
  fupQuestionsNewUiExperiment: FupQuestionsNewUiExperiment | null = null;
  rebrandedHairProductDetails: boolean = false;
  regimenVariantsScrollViewCollapseExperiment: RegimenVariantsScrollViewCollapseExperiment | null = null;
  regimenProductImageCarousel: RegimenProductImageCarouselExperiment = null;

  constructor(
    private readonly appWebBridge: AppWebBridgeService,
    private readonly windowRef: WindowRefService,
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
  ) {
    this.appWebBridge.requestAppVersion().then((version: number) => (this.appVersion = version));
    try {
      this.webAppVersion = this.appConfig.Shared.AppVersion;
      this.isInternalUser = this.conn.isInternalUser();
    } catch (err) {
      // Handle error here
    }
  }

  async enableParseInterceptor(): Promise<boolean> {
    const os = await this.appWebBridge.requestOSInformation();
    const isiOSDevice = os === 'iOS' || this.windowRef.isSafariBrowser();
    if (isiOSDevice) return true;
    if (this.isInternalUser) return true;
    if (this.appVersion >= this.experimentAndroidVersionForParseInterceptor) return true;
    return false;
  }

  get showNewInstructionVideo(): boolean {
    return this.appVersion >= this.experimentForVideoInstructions;
  }

  /**
   * Fetches and processes active user experiments
   * This function can be used to centralize experiment handling logic
   * @param username Optional username to fetch experiments for
   * @returns Promise<any[]> The fetched experiments array
   */
  async fetchActiveUserExperiment(username?: string): Promise<any[]> {
    try {
      const experiments = await this.conn.findUserActiveExperiments(username);
      // Reset experiment flags
      this.resetExperimentFlags();

      // Process experiments
      experiments?.forEach((experiment: any): void => {
        this.processExperiment(experiment);
      });

      this._experimentsLoadedResolve();
      return experiments || [];
    } catch (error) {
      this._experimentsLoadedResolve();
      return [];
    }
  }

  /**
   * Resets all experiment flags to their default values
   */
  private resetExperimentFlags(): void {
    this.applyCashExperiment = false;
    this.globalCacheExperiment = null;
    this.addToCartVariantSelection = false;
    this.videoCallExperiment = false;
    this.regimenReorderExperiment = null;
    this.reviewOrderPageExperiment = null;
    this.skinReportDrVideoExperiment = null;
    this.skinReportHeroIngredientsVariant = null;
    this.treatmentPlanDescriptionExperiment = null;
    this.prefillAddressShiprocketEnabled = false;
    this.newRegimenPricingExperiment = undefined;
    this.rootCausesExperiment = null;
    this.bottomNavConfigExperiment = null;
    this.shopDynamicBannerExperiment = null;
    this.npsFeedbackRevamp = false;
    this.isRetentionHomeRevampExperimentEnabled = false;
    this.retentionHomeRevampExperiment = null;
    this.diet1500Experiment = null;
    this.rootCausesExperiment = null;
    this.acqShop = null;
    this.recentlyViewedExperiment = null;
    this.followUpChatDefaultFirstAnswer = null;
    this.prepaidDiscountExperiment = null;
    this.fupQuestionsNewUiExperiment = null;
    this.rebrandedHairProductDetails = false;
    this.regimenRouteExperiment = false;
    this.regimenRouteVariant = {};
    this.regimenRehaulV2 = false;
    this.regimenPreparationTimerVariant = { activationDelayInSeconds: 120 };
    this.newInstructionUIExperiment = false;
    this.dietBannerV2 = false;
    this.dietPlanV2Experiment = false;
    this.regimenVariantsScrollViewCollapseExperiment = null;
    this.regimenProductImageCarousel = null;
  }

  /**
   * Processes individual experiment and sets appropriate flags
   * @param experiment The experiment object to process
   */
  private processExperiment(experiment: any): void {
    switch (experiment.key) {
      case 'apply_cash':
        this.applyCashExperiment = true;
        break;
      case 'enable_global_cache':
        this.globalCacheExperiment = experiment; // Store the full experiment object
        break;
      case 'add_to_cart_variant_selection':
        this.addToCartVariantSelection = true;
        break;
      case 'shop_restricted_product_pregnancy_teenager':
        this.restrictedShopForPregnancyAndTeenagerExperiment = experiment;
        break;
      case 'gender_translations':
        this.genderTranslationsEnabled = true;
        break;
      case 'optimise_paid_home_page':
        this.optimiseApiEnabled = true;
        break;
      case 'hide_incomplete_chat_bottomsheet':
        this.hideIncompleteChatBottomsheet = true;
        break;
      case 'side_photo_nudge_in_front':
        this.sidePhotoNudgeInFrontFace = experiment;
        break;
      case '3000_video_call':
        this.videoCallExperiment = true;
        break;
      case 'skin_report_dr_video':
        this.skinReportDrVideoExperiment = experiment;
        break;
      case 'skin_report_hero_ingredients':
        this.skinReportHeroIngredientsVariant = experiment?.variant;
        break;
      case 'treatment_plan_description':
        this.treatmentPlanDescriptionExperiment = experiment;
        break;
      case 'kit_reorder':
        this.regimenReorderExperiment = experiment;
        break;
      case 'review_order_page':
        this.reviewOrderPageExperiment = experiment;
        break;
      case 'prefill_address_shiprocket':
        this.prefillAddressShiprocketEnabled = true;
        break;
      case 'prepaid_discount':
        this.prepaidDiscountExperiment = experiment;
        break;
      case 'add_extra_regimen_products':
        this.newRegimenPricingExperiment = experiment;
        break;
      case 'share_payment_link':
        this.sharePaymentLink = true;
        break;
      case 'ret_homepage_revamp':
        this.isRetentionHomeRevampExperimentEnabled = true;
        this.retentionHomeRevampExperiment = experiment;
        break;
      case 'diet_1500':
        this.diet1500Experiment = experiment;
        break;
      case 'report_root_causes':
        this.rootCausesExperiment = experiment;
        break;
      case 'bottom_nav_config':
        this.bottomNavConfigExperiment = experiment;
        break;
      case 'shop_dynamic_banner':
        this.shopDynamicBannerExperiment = experiment;
        break;
      case 'regimen_route_refactor':
        this.regimenRouteExperiment = true;
        this.regimenRouteVariant = (experiment.variant as RegimenRouteVariant) || {};
        break;
      case 'regimen_rehaul_v2':
        this.regimenRehaulV2 = true;
        break;
      case 'tree_personalized_regimen_activation':
        this.regimenPreparationTimerVariant = experiment.variant as RegimenPreparationTimerVariant;
        break;
      case 'instruction_page_redesign':
        this.newInstructionUIExperiment = true;
        break;
      case 'diet_banner_v2':
        this.dietBannerV2 = true;
        break;
      case 'diet_plan_v2':
        this.dietPlanV2Experiment = true;
        break;
      case 'chat_skip_last_questions':
        this.chatSkipLastQuestionsExperiment = experiment;
        break;
      case 'chat_skip_last_questions_home_banner':
        this.chatSkipLastQuestionHomeBanner = experiment;
        break;
      case 'rebranded_hair_product_details':
        this.rebrandedHairProductDetails = true;
        break;
      case 'recently_viewed_visibility':
        this.recentlyViewedExperiment = experiment;
        break;
      case 'followup_chat_default_first_answer':
        this.followUpChatDefaultFirstAnswer = experiment;
        break;
      case 'fup_questions_new_UI':
        this.fupQuestionsNewUiExperiment = experiment;
        break;
      case 'acq_shop':
        this.acqShop = experiment;
        break;
      case 'NPS_feedback_revamp':
        this.npsFeedbackRevamp = true;
        break;
      case 'regimen_variants_scroll_view_collapse':
        this.regimenVariantsScrollViewCollapseExperiment = {
          hideAfterTopScroll: !!experiment?.variant?.hideAfterTopScroll,
        };
        break;
      case 'regimen_product_image_carousel':
        this.regimenProductImageCarousel = experiment;
        break;
      default:
        break;
    }
  }

  getAcqShopExperiment(): AcqShopExperiment | null {
    return this.acqShop;
  }

  getRootCausesExperiment(): ReportRootCauseExperiment {
    return this.rootCausesExperiment;
  }

  getRestrictedShopExperiment(): RestrictedProductForPregnancyAndTeenagerExperiment {
    return this.restrictedShopForPregnancyAndTeenagerExperiment ?? null;
  }

  getChatSkipLastQuestionsExperiment() {
    return this.chatSkipLastQuestionsExperiment ?? null;
  }

  getChatSkipLastQuestionHomeBanner() {
    return this.chatSkipLastQuestionHomeBanner ?? null;
  }

  getRecentlyViewedExperiment(): RecentlyViewedExperiment {
    return this.recentlyViewedExperiment ?? null;
  }

  isProductImageCarouselEnabledForClass(regimenClass: string): boolean {
    const variant = this.regimenProductImageCarousel?.variant;
    if (!variant) {
      return false;
    }
    return !!variant[regimenClass as keyof typeof variant];
  }

  getRetentionHomeRevampExperiment(): RetentionHomeRevampExperiment {
    return this.retentionHomeRevampExperiment ?? null;
  }

  // diet_1500: PRO diet plan pricing. Returns null when the experiment isn't active.
  getDietPlanProPricing(): { proPlanId: string; proPlanPrice: number } | null {
    const variant: any = this.diet1500Experiment?.variant;
    if (!variant) return null;
    const parsed: any = typeof variant === 'string' ? JSON.parse(variant) : variant;
    return { proPlanId: parsed?.proPlanId, proPlanPrice: parsed?.proPlanPrice };
  }

  processRegimenRehaulV1Experiment(
    experiment: RegimenRehaulV1Experiment,
    options?: { regimenClass?: string; userLanguage?: string; }
  ): RegimenRehaulV1Result {
    if (!experiment || !experiment.variant) {
      return {};
    }

    const result: RegimenRehaulV1Result = {};
    const variants = experiment.variant;

    if (variants.back_nudge_dr_call !== undefined) {
      result.nudgeDoctorCallOnBack = variants.back_nudge_dr_call;
    }

    if (variants.dr_banner_change !== undefined) {
      result.drBannerChange = variants.dr_banner_change;
    }

    if (variants.remove_variant !== undefined) {
      result.removeVariants = variants.remove_variant;
    }

    return result;
  }

  /**
   * Calculates whether to use CureSkin Cash based on experiment and user orderState
   * - If applyCashExperiment is false: always return true
   * - If applyCashExperiment is true:
   *   - If orderState is DELIVERED: return false
   *   - Otherwise: return true
   * @param orderState The user's orderState (e.g., 'DELIVERED', 'PROCESSING', etc.)
   * @returns boolean indicating whether to use CureSkin Cash
   */
  shouldUseCureSkinCash(orderState?: string): boolean {
    // If experiment is off, always use cash
    if (!this.applyCashExperiment) {
      return true;
    }

    // If experiment is on, check orderState
    // If orderState is DELIVERED, don't use cash
    if (orderState === this.appConfig.Shared.User.OrderState.DELIVERED) {
      return false;
    }

    // For any other orderState, use cash
    return true;
  }

  getBottomNavConfig(): BottomNavConfigExperiment {
    return this.bottomNavConfigExperiment;
  }

  isBottomNavConfigActive(): boolean {
    const exp = this.bottomNavConfigExperiment;
    return !!(exp?.enable && exp?.variant);
  }

  get sidePhotoNudgeInFrontFaceExperiment(): Record<string, unknown> {
    return this.sidePhotoNudgeInFrontFace ?? null;
  }

  getSkinReportDrVideoExperiment(): any {
    return this.skinReportDrVideoExperiment;
  }

  getSkinReportHeroIngredientsVariant(): { numberOfIngredients: number } {
    return this.skinReportHeroIngredientsVariant;
  }

  getShopDynamicBannerExperiment(): ShopDynamicBannerExperiment {
    return this.shopDynamicBannerExperiment ?? null;
  }

  getFollowUpChatDefaultFirstAnswer(): FollowUpChatDefaultFirstAnswerExperiment {
    return this.followUpChatDefaultFirstAnswer ?? null;
  }

  isFupQuestionsNewUiEnabled(): boolean {
    return !!this.fupQuestionsNewUiExperiment?.enable;
  }

  getFupQuestionsNewUiExperiment(): FupQuestionsNewUiExperiment | null {
    return this.fupQuestionsNewUiExperiment ?? null;
  }

  isNpsFeedbackRevampEnabled(): boolean {
    return this.npsFeedbackRevamp;
  }

  isDietPlanV2Enabled(): boolean {
    return this.dietPlanV2Experiment;
  }
}
